import { RESET_ALL as RESET_ON_LOGOUT, danger } from 'redux-store/reducers/common-reducer';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { initSubscription } from 'redux-store/reducers/cloudinaryVideos';

import { createReducer } from 'utils/redux';
import { API, get, patch } from 'utils/api';
import { logError } from 'utils/log';
import { t } from 'utils/language';
import { sortOldestFirst } from 'utils/list';
import { decodeToken, getAccessToken } from 'utils/token';
import { trackAccountDataUpdated } from '../../tracking/utils/tracking';
import { receiveRoles } from './receive-roles';

import { RECEIVE, RECEIVE_ROLES } from './constants';

import type { StateAccount } from 'types/State.type';
import type { Account, Organization } from 'types';

const { GET_CURRENT_USER, NEW_USERS } = API.END_POINTS.API;

const initialState: StateAccount = {
  currentAccount: null,
  agency: null,
  mapByAgencyOrganizationId: {},
  roles: []
};

function receiveCurrentAccount(account) {
  return {
    type: RECEIVE,
    payload: {
      account
    }
  };
}

export function currentAccountChanged(account: Account, currentOrganization?: Organization) {
  return (dispatch) => {
    dispatch(receiveCurrentAccount(account));
    trackAccountDataUpdated(account, currentOrganization);
  };
}

export function updateCurrentAccountBasedOnOrg(account: Account) {
  return (dispatch) => {
    dispatch(receiveCurrentAccount(account));
  };
}

export function currentAccountUpdated(account) {
  return (dispatch, getState) => {
    const currentAccount = getState().account.currentAccount;
    const { currentOrganization } = getState().organizations;
    const newAccount = {
      ...account,
      agency: account.agency,
      organizations: ('organizations' in account) ? account.organizations : currentAccount.organizations
    };
    dispatch(receiveCurrentAccount(newAccount));
    trackAccountDataUpdated(newAccount, currentOrganization);
  };
}

export const fetchCurrentAccount = () => (
  async (dispatch) => {
    try {
      const url = GET_CURRENT_USER;
      const response = await get(url);
      const data = response.data || [];
      const account = { ...response.data, organizations: sortOldestFirst(data.organizations) };

      dispatch(currentAccountChanged(account));

      if (window.app.env.FEATURE_SERVER_SIDE_EVENTS) {
        initSubscription();
      }

      const accessToken = getAccessToken();
      const roles = decodeToken(accessToken)?.roles ?? [];

      receiveRoles(roles);

      return account;
    } catch (error) {
      const { response, message } = error;

      if (response) {
        dispatch(danger([400, 401].includes(response.status) ? t('authentication_failed') : response.title));
      } else {
        dispatch(danger(message));
      }

      logError(error);

      throw error; // don't suppress error
    }
  });

export const reFetchCurrentAccountBasedOnSelectedOrg = () => {
  const url = GET_CURRENT_USER;

  return (dispatch) => get(url)
    .then((response : { data: Account }) => dispatch(updateCurrentAccountBasedOnOrg(response.data)))
    .catch((error) => {
      const { response, message } = error;
      if (response) {
        dispatch(danger([400, 401].includes(response.status) ? t('authentication_failed') : response.title));
      } else {
        dispatch(danger(message));
      }

      logError(error);

      throw error; // don't suppress error
    });
};

export function activateUserAccount(params) {
  const url = `${NEW_USERS}/${params.userId}`;
  const headers = {
    'Authorization': `${params.token}`,
    'X-Activation': 1
  };

  // doesn't really suppress but allows a custom auth header to take place
  const suppressAuth = true;

  return (dispatch) => {
    dispatch(showLoading(url));

    return patch(url, { headers }, { suppressAuth })
      .then(() => {
        dispatch(hideLoading(url));
      })
      .catch((error) => {
        const invalidTokenError = error.response && error.response.status >= 400;

        dispatch(danger(invalidTokenError ? params.invalidTokenMessage : error.message));
        dispatch(hideLoading(url));
        throw error;
      });
  };
}

export default createReducer<StateAccount, any>('account', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RECEIVE]: (state, { account }) => ({
    ...state,
    currentAccount: account,
    mapByAgencyOrganizationId: ((account.agency || {}).organizations || []).reduce((mapByAgencyOrganizationId, organization) => {
      // eslint-disable-next-line no-param-reassign
      mapByAgencyOrganizationId[organization.id] = true;
      return mapByAgencyOrganizationId;
    }, {})
  }),
  [RECEIVE_ROLES]: (state, { roles }) => ({
    ...state,
    roles
  })
});
